import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';

import { DocumentDataService } from '@shared/components/files/shared/document-data.service';
import { DownloadedExportsService } from '@shared/components/files/shared/downloaded-exports.service';
import { DocumentExportJob } from '@shared/components/files/shared/models/document-export-job.model';
import { DocumentExportJobStatus } from '@shared/components/files/shared/models/document-export-job-status.enum';
import { UserService } from '@user/user.service';

/**
 * Landing page for email download links (/oes/exports/:jobId). Uses
 * {@code GET /document-exports/{jobId}} as the single state source — including the
 * {@code deletedAt} field, which the backend exposes specifically so this route can render
 * a distinct "has been removed" message without parsing typed 410 payloads from the download
 * endpoint.
 *
 * <p>Flow:
 * <ul>
 *   <li>COMPLETED → trigger iframe download immediately and show a "download starting"
 *       confirmation with a back-to-files button.</li>
 *   <li>QUEUED / RUNNING → poll until terminal and re-evaluate.</li>
 *   <li>FAILED → render the backend {@code errorMessage}.</li>
 *   <li>EXPIRED → render the expired message.</li>
 *   <li>{@code deletedAt !== null} → render the "removed" message.</li>
 *   <li>404 → render the generic "not found" message.</li>
 * </ul>
 *
 * <p>Known v1 limitation: if a user clicks an email link while logged out, TokenCheckGuard
 * redirects to login but does not preserve the destination URL; after sign-in they land on
 * the home page. The Current Exports panel serves as the in-app fallback for that case.
 */
@Component({
  selector: 'oes-export-download',
  templateUrl: './export-download.component.html',
  styleUrls: ['./export-download.component.scss'],
})
export class ExportDownloadComponent implements OnInit, OnDestroy {

  readonly STATUS = DocumentExportJobStatus;

  loading = true;
  job: DocumentExportJob | null = null;
  resolvedState: 'LOADING' | 'DOWNLOADING' | 'DELETED' | 'EXPIRED' | 'FAILED' | 'NOT_FOUND' = 'LOADING';
  failureMessage: string | null = null;
  private organizationId: string | null = null;
  private jobId: string | null = null;

  private readonly destroyed$ = new Subject<void>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private documentDataService: DocumentDataService,
    private downloadedExportsService: DownloadedExportsService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.jobId = this.activatedRoute.snapshot.paramMap.get('jobId');
    if (!this.jobId) {
      this.resolvedState = 'NOT_FOUND';
      this.loading = false;
      return;
    }

    this.userService
      .getCurrentUser()
      .pipe(
        take(1),
        switchMap((user) => {
          this.organizationId = user.organization?.id ?? null;
          return this.documentDataService.getExportJob(this.organizationId, this.jobId);
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (job) => this.handleJob(job),
        error: () => this.handleNotFound(),
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  backToFiles(): void {
    this.router.navigate(['/oes/files']);
  }

  private handleJob(job: DocumentExportJob): void {
    this.job = job;

    if (job.deletedAt) {
      this.resolvedState = 'DELETED';
      this.loading = false;
      return;
    }

    switch (job.status) {
      case DocumentExportJobStatus.COMPLETED:
        this.startDownload();
        return;
      case DocumentExportJobStatus.QUEUED:
      case DocumentExportJobStatus.RUNNING:
        this.pollUntilTerminal();
        return;
      case DocumentExportJobStatus.FAILED:
        this.resolvedState = 'FAILED';
        this.failureMessage =
          job.errorMessage ||
          this.translateService.instant('files.export-download.failed');
        this.loading = false;
        return;
      case DocumentExportJobStatus.EXPIRED:
        this.resolvedState = 'EXPIRED';
        this.loading = false;
        return;
      default:
        this.resolvedState = 'NOT_FOUND';
        this.loading = false;
    }
  }

  private pollUntilTerminal(): void {
    if (!this.organizationId || !this.jobId) {
      return;
    }
    this.loading = true;
    this.documentDataService
      .pollExportJob(this.organizationId, this.jobId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (job) => {
          if (
            job.status === DocumentExportJobStatus.COMPLETED ||
            job.status === DocumentExportJobStatus.FAILED ||
            job.status === DocumentExportJobStatus.EXPIRED
          ) {
            this.handleJob(job);
          }
        },
        error: () => this.handleNotFound(),
      });
  }

  private startDownload(): void {
    if (!this.organizationId || !this.jobId) {
      return;
    }
    this.resolvedState = 'DOWNLOADING';
    this.loading = false;
    const jobIdSnapshot = this.jobId;
    this.documentDataService
      .downloadExportJob(this.organizationId, this.jobId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          // Iframe handoff complete — UI stays on the "download starting" view.
          this.downloadedExportsService.markDownloaded(jobIdSnapshot);
        },
        error: () => {
          // If the backend 410s or the iframe chokes, fall back to the expired message.
          this.resolvedState = 'EXPIRED';
        },
      });
  }

  private handleNotFound(): void {
    this.resolvedState = 'NOT_FOUND';
    this.loading = false;
  }
}
